import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Building2, CalendarRange, Lightbulb, Loader2, RefreshCw,
  Search, Stethoscope, TrendingDown, TrendingUp, Users, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityInfoBlock, EntityStatPill } from "@/components/companies/entity-ui";
import { formatCiroDate, formatCiroMoney } from "@/utils/ciroHelpers";
import {
  BarList,
  DonutChart,
  PaymentSplitChart,
  PeriodComparisonCard,
  StackedTrendChart,
  WeekdayChart,
} from "@/components/finance/ciroCharts";

const TREND_LABELS = { daily: "Günlük", monthly: "Aylık", yearly: "Yıllık" };

const CiroDashboardPage = ({
  apiBase, isDark, textClass, labelClass, cardClass, inputClass,
  companies = [], isSuperAdmin = false, userCompanyId = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("monthly");
  const [companyFilter, setCompanyFilter] = useState(isSuperAdmin ? "all" : String(userCompanyId || ""));
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [hospitalFilter, setHospitalFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);

  const scopedDoctors = useMemo(() => {
    if (!isSuperAdmin || companyFilter === "all") return doctors;
    return doctors.filter((d) => String(d.company_id) === String(companyFilter));
  }, [doctors, companyFilter, isSuperAdmin]);

  const scopedHospitals = useMemo(() => {
    if (!isSuperAdmin || companyFilter === "all") return hospitals;
    return hospitals.filter((h) => String(h.company_id) === String(companyFilter));
  }, [hospitals, companyFilter, isSuperAdmin]);

  const fetchMeta = useCallback(async () => {
    try {
      const params = {};
      if (isSuperAdmin && companyFilter && companyFilter !== "all") params.company_id = companyFilter;
      const [docRes, hospRes] = await Promise.all([
        axios.get(`${apiBase}/doctors`, { params }),
        axios.get(`${apiBase}/hospitals`, { params }),
      ]);
      setDoctors(docRes.data?.doctors || []);
      setHospitals(hospRes.data?.hospitals || []);
    } catch {
      setDoctors([]);
      setHospitals([]);
    }
  }, [apiBase, companyFilter, isSuperAdmin]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        dashboard: 1,
        trend_period: trendPeriod,
        search: searchTerm,
        doctor_id: doctorFilter,
        hospital_id: hospitalFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };
      if (isSuperAdmin && companyFilter !== "all") params.company_id = companyFilter;
      const res = await axios.get(`${apiBase}/ciro`, { params });
      setData(res.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dashboard yüklenemedi");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, companyFilter, doctorFilter, endDate, hospitalFilter, isSuperAdmin, searchTerm, startDate, trendPeriod]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleSearch = () => setSearchTerm(searchInput.trim());

  const kpis = data?.kpis || {};
  const totalCiro = Number(kpis.total_ciro) || 0;
  const hospitalShare = totalCiro > 0 ? ((Number(kpis.hospital_payments) || 0) / totalCiro) * 100 : 0;
  const deltaPct = Number(data?.period_comparison?.delta_pct) || 0;

  const doctorRows = useMemo(
    () => (data?.doctor_comparison || []).map((row) => ({
      ...row,
      subtitle: `${row.operation_count} işlem`,
    })),
    [data?.doctor_comparison]
  );

  const hospitalRows = useMemo(
    () => (data?.by_hospital || []).map((row) => ({
      ...row,
      subtitle: `${row.count} işlem`,
    })),
    [data?.by_hospital]
  );

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${textClass}`}>
              <BarChart3 className="w-5 h-5 text-emerald-500" />
              Ciro Dashboard
            </h2>
            <p className={`text-xs mt-1 ${labelClass}`}>
              Doktor, hastane ve agent kırılımlarıyla ciro örüntüleri, trendler ve dönem karşılaştırmaları
            </p>
            {data?.date_range?.min && data?.date_range?.max && (
              <p className={`text-[11px] mt-1 font-mono ${labelClass}`}>
                Veri aralığı: {formatCiroDate(data.date_range.min)} – {formatCiroDate(data.date_range.max)}
              </p>
            )}
          </div>
          <Button type="button" size="sm" variant="outline" onClick={fetchDashboard} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Yenile
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mt-4">
          <EntityStatPill label="Toplam Ciro" value={formatCiroMoney(kpis.total_ciro)} tone="emerald" isDark={isDark} />
          <EntityStatPill label="İşlem" value={Number(kpis.total_operations || 0).toLocaleString("tr-TR")} tone="violet" isDark={isDark} />
          <EntityStatPill label="Ort. İşlem" value={formatCiroMoney(kpis.avg_ticket)} tone="sky" isDark={isDark} />
          <EntityStatPill label="Hasta" value={Number(kpis.unique_patients || 0).toLocaleString("tr-TR")} tone="indigo" isDark={isDark} />
          <EntityStatPill label="Hastane Payı" value={`%${hospitalShare.toFixed(1)}`} tone="sky" isDark={isDark} />
          <EntityStatPill
            label="Dönem Değişimi"
            value={`${deltaPct >= 0 ? "+" : ""}${deltaPct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%`}
            tone={deltaPct >= 0 ? "emerald" : "violet"}
            isDark={isDark}
          />
        </div>
      </div>

      <div className={`rounded-xl border p-3 ${cardClass}`}>
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-2 items-end">
          {isSuperAdmin && (
            <div>
              <Label className={`text-xs ${labelClass}`}>Firma</Label>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tümü</SelectItem>
                  {companies.filter((c) => c.status === 1).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className={`text-xs ${labelClass}`}>Doktor</Label>
            <Select value={doctorFilter} onValueChange={setDoctorFilter}>
              <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tümü</SelectItem>
                {scopedDoctors.map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>{d.ad_soyad}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={`text-xs ${labelClass}`}>Hastane</Label>
            <Select value={hospitalFilter} onValueChange={setHospitalFilter}>
              <SelectTrigger className={`h-8 text-xs ${inputClass}`}><SelectValue /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tümü</SelectItem>
                {scopedHospitals.map((h) => (
                  <SelectItem key={h.id} value={String(h.id)}>{h.ad}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={`text-xs ${labelClass}`}>Başlangıç</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`h-8 text-xs ${inputClass}`} />
          </div>
          <div>
            <Label className={`text-xs ${labelClass}`}>Bitiş</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`h-8 text-xs ${inputClass}`} />
          </div>
          <div className="col-span-2 flex gap-2">
            <Input
              placeholder="Hasta, agent, işlem..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className={`h-8 text-xs ${inputClass}`}
            />
            <Button type="button" size="sm" onClick={handleSearch} className="h-8 shrink-0">
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className={`rounded-xl border p-12 flex items-center justify-center ${cardClass}`}>
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className={`rounded-xl border p-4 xl:col-span-2 ${isDark ? "border-slate-700 bg-slate-900/40" : "border-gray-200 bg-white"}`}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className={`flex items-center gap-2 text-sm font-semibold ${textClass}`}>
                  <TrendingUp className="w-4 h-4 text-blue-500" />
                  Hastane / Klinik Ciro Trendi
                </div>
                <div className={`inline-flex rounded-lg p-0.5 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                  {Object.entries(TREND_LABELS).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setTrendPeriod(id)}
                      className={`px-3 py-1 text-xs rounded-md transition-all ${
                        trendPeriod === id
                          ? isDark ? "bg-slate-700 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                          : isDark ? "text-slate-400 hover:text-slate-200" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <StackedTrendChart trend={data?.trend || []} period={trendPeriod} isDark={isDark} />
            </div>

            <EntityInfoBlock title="Dönem Karşılaştırması" icon={CalendarRange} tone="amber" isDark={isDark}>
              <PeriodComparisonCard comparison={data?.period_comparison} isDark={isDark} textClass={textClass} labelClass={labelClass} />
            </EntityInfoBlock>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <EntityInfoBlock title="Doktor Karşılaştırması" icon={Stethoscope} tone="violet" isDark={isDark}>
              <BarList items={doctorRows} isDark={isDark} subtitleKey="subtitle" tone="violet" />
            </EntityInfoBlock>

            <EntityInfoBlock title="Hastane Dağılımı" icon={Building2} tone="sky" isDark={isDark}>
              <BarList items={hospitalRows} isDark={isDark} subtitleKey="subtitle" tone="sky" />
            </EntityInfoBlock>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <EntityInfoBlock title="Agent Performansı" icon={Users} tone="emerald" isDark={isDark}>
              <BarList items={data?.breakdown?.agent || []} isDark={isDark} tone="emerald" />
            </EntityInfoBlock>

            <EntityInfoBlock title="Satışçı Performansı" icon={Wallet} tone="amber" isDark={isDark}>
              <BarList items={data?.breakdown?.seller || []} isDark={isDark} tone="amber" />
            </EntityInfoBlock>

            <EntityInfoBlock title="Ödeme Dağılımı" icon={Building2} tone="sky" isDark={isDark}>
              <PaymentSplitChart items={data?.payment_split || []} isDark={isDark} />
            </EntityInfoBlock>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <EntityInfoBlock title="Haftanın Günleri" icon={BarChart3} tone="indigo" isDark={isDark}>
              <WeekdayChart items={data?.by_weekday || []} isDark={isDark} />
            </EntityInfoBlock>

            <EntityInfoBlock title="İşlem / Bölge Dağılımı" icon={TrendingUp} tone="rose" isDark={isDark}>
              <DonutChart items={data?.by_procedure || []} isDark={isDark} />
            </EntityInfoBlock>

            <EntityInfoBlock title="Ödeme Yöntemleri" icon={Wallet} tone="sky" isDark={isDark}>
              <BarList items={data?.payment_methods || []} isDark={isDark} tone="sky" />
            </EntityInfoBlock>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <EntityInfoBlock title="Vücut Bölgesi Kırılımı" icon={Stethoscope} tone="emerald" isDark={isDark}>
              <BarList items={data?.by_body_region || []} isDark={isDark} tone="emerald" />
            </EntityInfoBlock>

            <EntityInfoBlock title="Örüntüler & İçgörüler" icon={Lightbulb} tone="amber" isDark={isDark}>
              {data?.insights?.length ? (
                <ul className="space-y-2.5">
                  {data.insights.map((line, idx) => (
                    <li
                      key={`${idx}-${line.slice(0, 24)}`}
                      className={`text-xs leading-relaxed flex gap-2 ${isDark ? "text-slate-300" : "text-gray-700"}`}
                    >
                      <span className={`mt-0.5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                        {deltaPct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                      </span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-xs ${labelClass}`}>Henüz yeterli veri yok — filtreleri genişletin veya ciro import edin.</p>
              )}
            </EntityInfoBlock>
          </div>
        </>
      )}
    </div>
  );
};

export default CiroDashboardPage;
